import React, { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Lock, LogOut, Unlock } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";

/** Quick screen lock with a 4-digit PIN.
 *  - First lock asks the user to set a PIN (stored in sessionStorage — cleared on logout/close).
 *  - Subsequent locks re-use the same PIN.
 *  - Forgot PIN → sign out. No password bypass, no persistence across sessions.
 */
export function LockButton() {
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    // expose global to allow keyboard shortcut / other places to trigger lock
    window.__mLock = () => setVisible(true);
    return () => { delete window.__mLock; };
  }, []);
  return (
    <>
      <Button variant="ghost" size="icon" onClick={()=>setVisible(true)} className="h-9 w-9" title="قفل الشاشة" data-testid="lock-screen-btn">
        <Lock className="w-4 h-4" />
      </Button>
      {visible && <LockOverlay onUnlock={()=>setVisible(false)} />}
    </>
  );
}

function LockOverlay({ onUnlock }) {
  const { user, logout } = useAuth();
  const [pin, setPin] = useState("");
  const [confirmPin, setConfirmPin] = useState("");
  const stored = sessionStorage.getItem("lock_pin");
  const isSetup = !stored;

  const setup = () => {
    if (!/^\d{4,6}$/.test(pin)) return toast.error("أدخل 4 إلى 6 أرقام");
    if (pin !== confirmPin) return toast.error("الرمزان غير متطابقين");
    sessionStorage.setItem("lock_pin", pin);
    toast.success("تم قفل الشاشة");
    setPin(""); setConfirmPin("");
  };

  const unlock = () => {
    if (pin === stored) {
      toast.success("مرحباً بعودتك");
      setPin("");
      onUnlock();
    } else {
      toast.error("رمز خاطئ");
      setPin("");
    }
  };

  const onSignOut = () => {
    sessionStorage.removeItem("lock_pin");
    logout();
  };

  return (
    <div className="fixed inset-0 bg-black/85 backdrop-blur-sm z-[100] grid place-items-center p-4" data-testid="lock-overlay">
      <div className="bg-card border border-border rounded-lg p-8 max-w-sm w-full text-center shadow-2xl">
        <div className="w-16 h-16 mx-auto rounded-full bg-primary/10 text-primary grid place-items-center mb-4">
          <Lock className="w-8 h-8" />
        </div>
        <h2 className="text-xl font-heading font-bold">{isSetup ? "قفل الشاشة" : "شاشة مقفلة"}</h2>
        <p className="text-sm text-muted-foreground mt-1">{user?.name}</p>
        <p className="text-xs text-muted-foreground mt-3">
          {isSetup
            ? "اختر رمز PIN من 4-6 أرقام. لن يُطلب منك كلمة المرور — فقط الرمز حتى تسجل الخروج."
            : "أدخل رمز PIN لفتح القفل."}
        </p>

        <div className="space-y-3 mt-6" onKeyDown={(e)=>{ if(e.key==="Enter") isSetup ? setup() : unlock(); }}>
          <Input
            type="password" inputMode="numeric" maxLength={6} autoFocus
            value={pin} onChange={(e)=>setPin(e.target.value.replace(/\D/g,""))}
            placeholder="●●●●" className="text-center text-2xl tracking-[0.5em] h-14"
            data-testid="lock-pin-input"
          />
          {isSetup && (
            <Input
              type="password" inputMode="numeric" maxLength={6}
              value={confirmPin} onChange={(e)=>setConfirmPin(e.target.value.replace(/\D/g,""))}
              placeholder="أعد إدخال الرمز" className="text-center text-2xl tracking-[0.5em] h-14"
              data-testid="lock-pin-confirm"
            />
          )}
        </div>

        <div className="flex gap-2 mt-6">
          {isSetup ? (
            <Button onClick={setup} className="flex-1 gap-1" data-testid="lock-setup-btn">
              <Lock className="w-4 h-4" /> قفل
            </Button>
          ) : (
            <Button onClick={unlock} className="flex-1 gap-1" data-testid="lock-unlock-btn">
              <Unlock className="w-4 h-4" /> فتح القفل
            </Button>
          )}
          <Button variant="outline" onClick={onSignOut} className="gap-1 text-destructive" data-testid="lock-signout-btn">
            <LogOut className="w-4 h-4" /> نسيت الرمز
          </Button>
        </div>
      </div>
    </div>
  );
}

/** Persistent guard mounted at the app root: shows the lock overlay whenever
 *  sessionStorage.lock_active is "1". Setting this key locks the app.
 *  Also auto-locks after `idleMinutes` of inactivity when a PIN has been set. */
export function LockGuard({ idleMinutes = 5 }) {
  const [locked, setLocked] = useState(() => sessionStorage.getItem("lock_active") === "1");
  useEffect(() => {
    window.__mLockNow = () => { sessionStorage.setItem("lock_active", "1"); setLocked(true); };
    return () => { delete window.__mLockNow; };
  }, []);

  // Idle auto-lock: only if a PIN was set. Resets on any activity.
  useEffect(() => {
    let timer;
    const reset = () => {
      clearTimeout(timer);
      if (locked) return; // already locked
      if (!sessionStorage.getItem("lock_pin")) return; // no PIN set yet, don't force lock
      timer = setTimeout(() => {
        sessionStorage.setItem("lock_active", "1");
        setLocked(true);
      }, Math.max(1, idleMinutes) * 60 * 1000);
    };
    const events = ["mousemove", "keydown", "touchstart", "click", "wheel", "scroll"];
    events.forEach((e) => window.addEventListener(e, reset, { passive: true }));
    reset();
    return () => {
      clearTimeout(timer);
      events.forEach((e) => window.removeEventListener(e, reset));
    };
  }, [locked, idleMinutes]);

  if (!locked) return null;
  return <LockOverlay onUnlock={() => { sessionStorage.removeItem("lock_active"); setLocked(false); }} />;
}
